import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type PropsWithChildren,
} from 'react';
import {
  type ResetSource,
  type RxDbConnectionService,
  type RxDbPhase,
  type RxDbSnapshot,
  type UnavailableReason,
} from './rxDbConnectionService';
import { type RxDbDataService } from './rxDbDataService';
import { waitForTabVisible } from './rxDbInstance';
import RxDBSetupNotificationAlert from './RxDBSetupNotificationAlert';

type RxDbConnectionProps = Pick<
  RxDbConnectionService,
  | 'start'
  | 'dispose'
  | 'reset'
  | 'wipeForLogout'
  | 'status$'
  | 'refreshTabPresence'
>;

type OfflineDbContextValue = {
  connection: RxDbConnectionProps;
  data: RxDbDataService;
};

const OfflineDbContext = createContext<OfflineDbContextValue | null>(null);

export const STUCK_NOTICE_MS = 4_000;

type LoadStage = 'normal' | 'stuck';

const useOfflineDbSnapshotFrom = (
  connection: RxDbConnectionProps
): RxDbSnapshot => {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const sub = connection.status$.subscribe(() => onStoreChange());
      return () => sub.unsubscribe();
    },
    [connection]
  );

  const getSnapshot = useCallback((): RxDbSnapshot => {
    let current: RxDbSnapshot | undefined;
    connection.status$
      .subscribe((snapshot) => {
        current = snapshot;
      })
      .unsubscribe();
    if (!current) {
      throw new Error(
        'RxDbConnectionService.status$ did not emit synchronously on subscribe'
      );
    }
    return current;
  }, [connection]);

  return useSyncExternalStore(subscribe, getSnapshot);
};

const messageForUnavailable = (
  reason: Exclude<UnavailableReason, { kind: 'disposed' }>
): string => {
  switch (reason.kind) {
    case 'init-timeout':
      return 'Offline database is taking longer than expected to start. Please refresh or reset.';
    case 'reload-blocked':
      return 'Offline database was reset but this page did not reload. Please refresh your browser.';
    case 'unknown':
      return reason.detail instanceof Error
        ? reason.detail.message
        : 'Offline database is unavailable. Please refresh or reset.';
  }
};

const errorForPhase = (phase: RxDbPhase): Error | null =>
  phase.status === 'unavailable' && phase.reason.kind !== 'disposed'
    ? new Error(messageForUnavailable(phase.reason))
    : null;

export type OfflineDbProviderProps = PropsWithChildren<{
  connection: RxDbConnectionProps;
  data: RxDbDataService;
}>;

export const OfflineDbProvider: React.FC<OfflineDbProviderProps> = ({
  children,
  connection,
  data,
}) => {
  const connectionRef = useRef(connection);
  const dataRef = useRef(data);
  const [loadStage, setLoadStage] = useState<LoadStage>('normal');

  const contextValue = useMemo<OfflineDbContextValue>(
    () => ({ connection: connectionRef.current, data: dataRef.current }),
    []
  );

  const snapshot = useOfflineDbSnapshotFrom(connectionRef.current);
  const error = errorForPhase(snapshot.phase);

  useEffect(() => {
    const conn = connectionRef.current;
    conn.start();
    return () => {
      conn.dispose();
    };
  }, []);

  useEffect(() => {
    const isActive = snapshot.phase.status === 'active';
    let cancelled = false;
    let stuckTimeoutId: ReturnType<typeof setTimeout> | null = null;
    waitForTabVisible().then(() => {
      if (cancelled) {
        return;
      }

      if (stuckTimeoutId !== null) {
        clearTimeout(stuckTimeoutId);
      }

      if (!isActive) {
        stuckTimeoutId = setTimeout(() => {
          connectionRef.current.refreshTabPresence().catch(() => undefined);
          setLoadStage('stuck');
        }, STUCK_NOTICE_MS);
      }

      if (isActive) {
        setLoadStage('normal');
      }
    });

    return () => {
      cancelled = true;
      if (stuckTimeoutId !== null) {
        clearTimeout(stuckTimeoutId);
      }
    };
  }, [snapshot.phase.status]);

  const handleReset = useCallback(
    () => connectionRef.current.reset('error-screen'),
    []
  );

  const alertMessage = useMemo(() => {
    const tabsCount = snapshot.tabPresence?.otherTabCount ?? 0;
    const pluralSuffix = tabsCount === 1 ? '' : 's';

    const STUCK_MESSAGE = tabsCount
      ? [
          `You have ${tabsCount} other Airship tab${pluralSuffix} open.`,
          `Try clicking into the tab with a 👑 in the title as that is the primary tab.`,
        ]
      : [
          'If this is taking a long time, it may be because you have other Airship tabs open.',
          'Try closing other Airship tabs that you have open.',
        ];

    return loadStage !== 'stuck'
      ? undefined
      : [
          ...STUCK_MESSAGE,
          `status: ${snapshot.phase.status}`,
          `tab type: ${snapshot.leadership}`,
        ];
  }, [snapshot, loadStage]);

  return (
    <OfflineDbContext.Provider value={contextValue}>
      {snapshot.phase.status === 'active' ? (
        children
      ) : (
        <RxDBSetupNotificationAlert
          error={error}
          resetDb={handleReset}
          alertMessage={alertMessage}
        />
      )}
    </OfflineDbContext.Provider>
  );
};

const useOfflineDbContext = (hookName: string): OfflineDbContextValue => {
  const context = useContext(OfflineDbContext);
  if (!context) {
    throw new Error(`${hookName} must be used within an OfflineDbProvider`);
  }
  return context;
};

export const useOfflineDbData = (): RxDbDataService =>
  useOfflineDbContext('useOfflineDbData').data;

export const useOfflineDbStatus = (): RxDbSnapshot =>
  useOfflineDbSnapshotFrom(
    useOfflineDbContext('useOfflineDbStatus').connection
  );

export const useOfflineDbReset = (
  source: ResetSource
): (() => Promise<void>) => {
  const { connection } = useOfflineDbContext('useOfflineDbReset');
  return useCallback(() => connection.reset(source), [connection, source]);
};

export const useOfflineDbLogoutWipe = (): (() => Promise<void>) => {
  const { connection } = useOfflineDbContext('useOfflineDbLogoutWipe');
  return useCallback(() => connection.wipeForLogout(), [connection]);
};
